import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Divider,
} from '@mui/material';
import { CloudUpload, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { importApi } from '../services/api';

function ImportPage() {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [jobs, setJobs] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const data = await importApi.getJobs();
      setJobs(data);
    } catch (err) {
      console.error('Failed to load import jobs:', err);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    setError(null);
    setResult(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      // Expect either { transactions: [...] } or just an array
      const transactions = Array.isArray(data) ? data : (data.transactions || data.purchaseOrders);

      if (!transactions || !Array.isArray(transactions)) {
        throw new Error('Invalid file format. Expected a JSON array or { transactions: [...] }');
      }

      const importResult = await importApi.importTransactions(transactions);
      setResult(importResult);
      await loadJobs();
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="page-container">
      <Paper sx={{ p: 3, width: '100%', maxWidth: 900 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Import Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Import transaction data extracted from QuickBooks. Upload a JSON file containing
          transaction metadata (type, ref number, vendor/customer, date, amount, etc.).
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h6" gutterBottom>Import Transactions</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a JSON file with the following format:
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5', fontFamily: 'monospace', fontSize: '0.85rem' }}>
          {`[
  {
    "transactionType": "PurchaseOrder",
    "refNumber": "276087",
    "entityName": "Dell Federal Systems LP",
    "entityType": "Vendor",
    "txnDate": "2024-01-15",
    "amount": 1500.00,
    "memo": "Office supplies Q1",
    "qbTxnId": "ABC-123"
  }
]`}
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
          >
            {importing ? 'Importing...' : 'Upload JSON File'}
          </Button>
          {importing && <LinearProgress sx={{ flex: 1 }} />}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {result && (
          <Alert
            severity={result.errors > 0 ? 'warning' : 'success'}
            sx={{ mb: 2 }}
            onClose={() => setResult(null)}
          >
            Imported {result.imported} of {result.total} transactions.
            {result.errors > 0 && ` ${result.errors} errors.`}
          </Alert>
        )}
      </Paper>

      {/* Import History */}
      {jobs.length > 0 && (
        <Paper sx={{ width: '100%', maxWidth: 900, position: 'relative' }}>
          {/* Gradient scroll hint */}
          <Box sx={{ display: { xs: 'block', sm: 'none' }, position: 'absolute', right: 0, top: 0, bottom: 0, width: 36, background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.85))', pointerEvents: 'none', zIndex: 1, borderRadius: '0 4px 4px 0' }} />
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6">Import History</Typography>
          </Box>
          <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Records</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Files</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Errors</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {job.createdAt ? format(new Date(job.createdAt), 'MM/dd/yyyy HH:mm') : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={job.status === 'completed' ? <CheckCircle /> : job.status === 'failed' ? <ErrorIcon /> : undefined}
                        label={job.status}
                        color={job.status === 'completed' ? 'success' : job.status === 'failed' ? 'error' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{job.importedRecords}/{job.totalRecords}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{job.importedFiles}/{job.totalFiles}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {job.errors ? (Array.isArray(job.errors) ? job.errors.length : '—') : 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </div>
  );
}

export default ImportPage;
